import React from 'react';
import { X, User, Info, Settings, LogOut, Mail, Star, Book, Target, BookOpen, Building, Heart, Newspaper } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';
import UserAvatar from '../auth/UserAvatar';

interface SlidingMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SlidingMenu({ isOpen, onClose }: SlidingMenuProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/');
      onClose();
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={onClose}
        />
      )}

      <div className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 ease-in-out z-50 ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold text-gray-500 ">Menu</h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full text-gray-500 "
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {user && (
            <div className="p-4 border-b">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-gray-500">
                  <UserAvatar email={user.email || ''} imageUrl={user.photoURL || ''} size="sm" />
                </div>
                <div>
                  <div className="text-gray-500 font-medium">{user.email}</div>
                  <div className="text-sm text-gray-500">Member since {new Date(user.metadata.creationTime).getFullYear()}</div>
                </div>
              </div>
            </div>
          )}

          <nav className="flex-1 overflow-y-auto p-4">
            <Link
              to="/blog"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                location.pathname.startsWith('/blog')
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={onClose}
            >
              <BookOpen className="h-5 w-5" />
              <span>Blog</span>
            </Link>

            <Link
              to="/news"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                location.pathname.startsWith('/news')
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={onClose}
            >
              <Newspaper className="h-5 w-5" />
              <span>News</span>
            </Link>

            <Link
              to="/real-estate"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                location.pathname.startsWith('/real-estate')
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={onClose}
            >
              <Building className="h-5 w-5" />
              <span>Real Estate</span>
            </Link>

            <Link
              to="/about"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                location.pathname === '/about'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={onClose}
            >
              <Info className="h-5 w-5" />
              <span>About Us</span>
            </Link>

            <Link
              to="/contact"
              className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                location.pathname === '/contact'
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={onClose}
            >
              <Mail className="h-5 w-5" />
              <span>Contact Us</span>
            </Link>

            {user ? (
              <>
                <Link
                  to="/profile"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                    location.pathname === '/profile'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </Link>

                <Link
                  to="/wishlist"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                    location.pathname === '/wishlist'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Star className="h-5 w-5" />
                  <span>My Wishlists</span>
                </Link>

                <Link
                  to="/wishlist/funds"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ml-4 ${
                    location.pathname === '/wishlist/funds'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Star className="h-4 w-4" />
                  <span>Mutual Funds</span>
                </Link>

                <Link
                  to="/wishlist/stocks"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ml-4 ${
                    location.pathname === '/wishlist/stocks'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Star className="h-4 w-4" />
                  <span>Stocks</span>
                </Link>

                <Link
                  to="/favorite-properties"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ml-4 ${
                    location.pathname === '/favorite-properties'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Heart className="h-4 w-4" />
                  <span>Favorite Properties</span>
                </Link>

                <Link
                  to="/my-quotes"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                    location.pathname === '/my-quotes'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Book className="h-5 w-5" />
                  <span>My Wisdom Quotes</span>
                </Link>

                <Link
                  to="/financial-goals"
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                    location.pathname === '/financial-goals'
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={onClose}
                >
                  <Target className="h-5 w-5" />
                  <span>Financial Goals</span>
                </Link>
                
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 text-left"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg mb-2 ${
                  location.pathname === '/login'
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                onClick={onClose}
              >
                <LogOut className="h-5 w-5" />
                <span>Login</span>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </>
  );
}